import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import EditCollectionPopup from '../popups/EditCollectionPopup';
import DeleteConfirmationPopup from '../popups/DeleteConfirmationPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faLayerGroup, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { authFetch } from '../../utils/auth';

const CollectionsList = ({ refreshTrigger, onCollectionSelect, onCollectionUpdated, onCreateCollection, onUpdateCollection, createCollectionTooltip, collections: propCollections }) => {
  const [collections, setCollections] = useState([]);
  const [editingCollection, setEditingCollection] = useState(null);
  const [deletingCollection, setDeletingCollection] = useState(null);
  const { accounts } = useMsal();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        // Use the API utility with the new path structure
        const response = await authFetch('/collections');
        
        if (response.ok) {
          const data = await response.json();
          setCollections(data);
        } else {
          console.error('Failed to fetch collections');
        }
      } catch (error) {
        console.error("Error fetching collections:", error);
      }
    };

    fetchCollections();
  }, [accounts, refreshTrigger]);

  useEffect(() => {
    if (propCollections) {
      setCollections(propCollections);
    }
  }, [propCollections]);

  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
  };

  const handleDeleteCollection = async (collectionId) => {
    setDeletingCollection(collectionId);
  };

  const confirmDeleteCollection = async () => {
    try {
      const response = await authFetch(`/collections/${deletingCollection}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        const updatedCollections = collections.filter(c => c._id !== deletingCollection);
        setCollections(updatedCollections);
        onCollectionUpdated(updatedCollections);
      } else {
        console.error('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
    }
    setDeletingCollection(null);
  };

  const handleCollectionUpdated = (updatedCollection) => {
    const updatedCollections = collections.map(c => 
      c._id === updatedCollection._id ? updatedCollection : c
    );
    setCollections(updatedCollections);
    onCollectionUpdated(updatedCollections);
    onUpdateCollection(updatedCollection);
  };

  return (
    <div>
      <div className="flex flex-col mb-4 pb-2 border-b border-gray-200">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faLayerGroup} className="mr-2 text-gray-600" />
            <h3 className="text-lg font-semibold">{t('collectionsList.collections')}</h3>
          </div>
          <button 
            onClick={onCreateCollection}
            className="bg-[#F3F3EE] rounded text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
            style={{ borderRadius: '0.25rem', width: '1.5rem', height: '1.5rem' }}
            title={createCollectionTooltip}
          >
            <FontAwesomeIcon icon={faPlus} className="text-sm" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-1">{t('collectionsList.subtitle')}</p>
      </div>
      {collections.length > 0 ? (
        <ul>
          {collections.map((collection) => (
            <li 
              key={collection._id} 
              className="mb-2 rounded bg-[#F3F3EE]"
            >
              <div 
                className="p-2 cursor-pointer"
                onClick={() => onCollectionSelect(collection._id)}
              >
                <h4 className="font-semibold">{collection.title}</h4>
                <p className="text-sm text-gray-600">{collection.description}</p>
              </div>
              <div className="px-2 py-1 flex justify-between items-center text-xs text-gray-400">
                <span>
                  {collection.conversations.length} {t('collectionsList.conversationsLabel', { count: collection.conversations.length })}
                </span>
                <div className="space-x-2">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCollection(collection);
                    }}
                    className="text-text-muted hover:text-primary transition-colors"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCollection(collection._id);
                    }}
                    className="text-text-muted hover:text-error transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('collectionsList.noCollections')}</p>
      )}
      {editingCollection && (
        <EditCollectionPopup
          collection={editingCollection}
          onClose={() => setEditingCollection(null)}
          onCollectionUpdated={handleCollectionUpdated}
        />
      )}
      {deletingCollection && (
        <DeleteConfirmationPopup
          onConfirm={confirmDeleteCollection}
          onCancel={() => setDeletingCollection(null)}
        />
      )}
    </div>
  );
};

export default CollectionsList;
